import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  ChevronRight, 
  Clock, 
  Calendar, 
  ChevronDown, 
  ChevronUp,
  CheckCircle2,
  Lightbulb,
  ArrowRight,
  FileText,
  Target,
  Layers,
  AlertTriangle,
  Camera,
  Eye,
  Phone,
  Shield,
  Home,
  Thermometer,
  Droplets,
  Sun,
  HelpCircle,
  Ruler,
  ExternalLink
} from 'lucide-react';

const HouseCracksGuide = () => {
  const [activeSection, setActiveSection] = useState('');
  const [openFaq, setOpenFaq] = useState(null);

  // Table of contents
  const sections = [
    { id: 'section-1', title: "Ne paniquez pas — mais ne négligez pas" },
    { id: 'section-2', title: "Les différents types de fissures" },
    { id: 'section-3', title: "Les signaux qui doivent alerter" },
    { id: 'section-4', title: "Les causes principales" },
    { id: 'section-5', title: "Que faire concrètement ?" },
    { id: 'section-6', title: "Le rôle de l'étude de sol (G5)" },
    { id: 'section-7', title: "Les solutions de réparation" },
    { id: 'section-8', title: "Assurance et sécheresse" },
    { id: 'section-9', title: "Questions fréquentes" },
  ];

  const faqs = [
    {
      question: "Une fissure fine est-elle forcément sans danger ?",
      answer: "Pas forcément — tout dépend du contexte et de l'évolution. Une microfissure stable est généralement bénigne, mais une fissure fine récente qui s'élargit peut être le premier signe d'un mouvement. En cas de doute, faites évaluer."
    },
    {
      question: "Comment savoir si ma fissure bouge ?",
      answer: "Marquez ses extrémités au crayon avec la date, et observez sur plusieurs semaines/mois. Il existe aussi des témoins (jauges) que pose un professionnel pour mesurer précisément l'évolution."
    },
    {
      question: "Ma maison est fissurée après une sécheresse, que faire ?",
      answer: "Contactez votre mairie : seule une reconnaissance de catastrophe naturelle (arrêté CatNat) permet d'activer la garantie de votre assurance. En parallèle, faites diagnostiquer les fissures."
    },
    {
      question: "Qui contacter pour un diagnostic ?",
      answer: "Un bureau d'études géotechniques (pour la cause liée au sol), un expert en bâtiment ou un expert fissures indépendant. Pour des travaux de stabilisation, une entreprise de fondations spéciales."
    }
  ];

  // Track scroll for active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  };

  // Schema.org JSON-LD
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Fissures maison : quand s'inquiéter et que faire ? | Guide complet",
    "description": "Fissure sur un mur de votre maison : est-ce grave ? Types de fissures, signaux d'alerte, causes (sécheresse RGA), solutions et assurance. Le guide pour y voir clair.",
    "image": "https://geots.consulting/og-images/guide-fissures-maison.jpg",
    "author": {
      "@type": "Organization",
      "name": "GEOTS Consulting"
    },
    "publisher": {
      "@type": "Organization",
      "name": "GEOTS Consulting",
      "logo": {
        "@type": "ImageObject",
        "url": "https://geots.consulting/logo.png"
      }
    },
    "datePublished": "2026-07-11",
    "dateModified": "2026-07-11"
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  // Alert signs
  const alertSigns = [
    { text: "Fissure traversante : visible des deux côtés du mur (intérieur ET extérieur)", icon: Eye },
    { text: "Fissure qui évolue : elle s'élargit ou s'allonge au fil des semaines", icon: Ruler },
    { text: "Fissure large (> 1-2 cm) ou en escalier sur un mur porteur", icon: AlertTriangle },
    { text: "Portes et fenêtres qui coincent soudainement", icon: Home },
    { text: "Déformation visible d'un plancher, plafond ou encadrement", icon: Layers },
    { text: "Fissure horizontale au niveau d'un plancher", icon: Target },
  ];

  // Causes
  const causes = [
    { 
      title: "Sécheresse et retrait-gonflement des argiles (RGA)", 
      desc: "La cause n°1 des fissures graves. Les sols argileux gonflent avec l'humidité et se rétractent en période de sécheresse. Des millions de maisons sont concernées en France.",
      icon: Sun,
      color: "orange",
      link: "https://www.georisques.gouv.fr/risques/retrait-gonflement-des-argiles"
    },
    { 
      title: "Tassement différentiel", 
      desc: "Quand une partie des fondations s'enfonce plus que l'autre (sol hétérogène, extension récente, fondations sous-dimensionnées, racines d'arbres).",
      icon: Layers,
      color: "red"
    },
    { 
      title: "Variations thermiques", 
      desc: "La dilatation et contraction des matériaux, surtout sur les façades exposées au sud. Souvent superficiel.",
      icon: Thermometer,
      color: "blue"
    },
    { 
      title: "Infiltrations d'eau", 
      desc: "Elles dégradent le mortier et créent des fissures de retrait.",
      icon: Droplets,
      color: "cyan"
    },
  ];

  return (
    <>
      <Helmet>
        <title>Fissures maison : quand s'inquiéter et que faire ? | GEOTS</title>
        <meta name="description" content="Fissure sur un mur de votre maison : est-ce grave ? Types de fissures, signaux d'alerte, causes (sécheresse RGA), solutions et assurance. Le guide pour y voir clair." />
        <link rel="canonical" href="https://geots.consulting/guides/fissures-maison-quand-s-inquieter" />
        <meta property="og:title" content="Fissures maison : quand s'inquiéter et que faire ? | Guide complet" />
        <meta property="og:description" content="Fissure sur un mur de votre maison : est-ce grave ? Types de fissures, signaux d'alerte, causes (sécheresse RGA), solutions et assurance." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://geots.consulting/guides/fissures-maison-quand-s-inquieter" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Breadcrumb */}
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 py-3">
            <nav className="flex items-center gap-2 text-sm text-gray-600">
              <Link to="/" className="hover:text-[#635BFF]">Accueil</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/guides" className="hover:text-[#635BFF]">Guides</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-[#0A2540] font-medium">Fissures maison</span>
            </nav>
          </div>
        </div>

        {/* Hero Section */}
        <header className="bg-gradient-to-br from-[#0A2540] to-[#1a3a5c] text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl">
              <div className="flex items-center gap-4 mb-6 flex-wrap">
                <span className="bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                  GUIDE PRATIQUE
                </span>
                <span className="text-gray-300 text-sm flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Mis à jour le 11 juillet 2026
                </span>
                <span className="text-gray-300 text-sm flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  12 min de lecture
                </span>
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-6 leading-tight">
                Fissures dans une maison :
                <span className="text-[#00D4FF]"> quand s'inquiéter et que faire ?</span>
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                Vous avez repéré une fissure sur un mur de votre maison et vous vous demandez si c'est grave ? 
                Vous avez raison de vous poser la question — sans céder à la panique. Toutes les fissures ne se valent pas : 
                certaines sont bénignes, d'autres signalent un vrai problème de structure. 
                Ce guide vous aide à y voir clair et à savoir quand faire appel à un professionnel.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link 
                  to="/annuaire/ingenierie-etudes-geotechniques" 
                  className="bg-[#635BFF] hover:bg-[#5248e5] text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors"
                >
                  Trouver un expert
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link 
                  to="/etude-geotechnique" 
                  className="bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors"
                >
                  Demander un diagnostic
                </Link>
              </div>
            </div>
          </div>
        </header>

        <div className="container mx-auto px-4 py-12">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Sidebar - Table of Contents */}
            <aside className="lg:w-72 flex-shrink-0">
              <div className="sticky top-24 bg-white rounded-xl shadow-sm border p-6">
                <h3 className="font-bold text-[#0A2540] mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-[#635BFF]" />
                  Sommaire
                </h3>
                <nav className="space-y-2">
                  {sections.map((section, index) => (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-start gap-2 ${
                        activeSection === section.id
                          ? 'bg-[#635BFF]/10 text-[#635BFF] font-medium'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <span className="text-xs font-bold text-gray-400 mt-0.5">{index + 1}.</span>
                      {section.title}
                    </button>
                  ))}
                </nav>

                {/* Links to related guides */}
                <div className="mt-6 pt-6 border-t">
                  <p className="text-xs text-gray-500 mb-2">Guides connexes</p>
                  <Link 
                    to="/guides/comment-choisir-etude-geotechnique"
                    className="text-sm text-[#635BFF] hover:underline flex items-center gap-1 mb-2"
                  >
                    <FileText className="w-4 h-4" />
                    Études géotechniques (G5)
                  </Link>
                  <Link 
                    to="/guides/comment-choisir-fondations-speciales"
                    className="text-sm text-[#635BFF] hover:underline flex items-center gap-1"
                  >
                    <Layers className="w-4 h-4" />
                    Fondations spéciales
                  </Link>
                </div>
              </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 max-w-3xl">
              <article className="prose prose-lg max-w-none">
                
                {/* Section 1 */}
                <section id="section-1" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">1</span>
                    D'abord, ne paniquez pas — mais ne négligez pas
                  </h2>
                  
                  <p className="text-gray-700 mb-6">
                    La plupart des fissures qui apparaissent sur une maison sont <strong>superficielles et sans danger</strong>. 
                    Mais certaines traduisent un mouvement des fondations ou de la structure, et doivent être prises au sérieux.
                  </p>

                  <div className="bg-[#00D4FF]/10 border border-[#00D4FF]/30 rounded-xl p-6 mb-8">
                    <Lightbulb className="w-8 h-8 text-[#00D4FF] mb-3" />
                    <p className="text-[#0A2540]">
                      <strong>La bonne attitude tient en trois mots :</strong> <strong>observer, mesurer, faire évaluer</strong>. 
                      Ce guide vous donne des repères pour comprendre ce que vous voyez — mais lui seul ne remplace jamais 
                      le diagnostic d'un professionnel, qui est indispensable dès qu'un doute existe sur la structure.
                    </p>
                  </div>
                </section>

                {/* Section 2 - Types */}
                <section id="section-2" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">2</span>
                    Les différents types de fissures
                  </h2>

                  <p className="text-gray-700 mb-6">
                    On distingue les fissures selon leur largeur et leur nature :
                  </p>

                  <div className="bg-white rounded-xl border overflow-hidden mb-8">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#0A2540]">Type</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#0A2540]">Largeur</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#0A2540]">Niveau de gravité</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        <tr className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-700">Microfissures</td>
                          <td className="px-4 py-3 text-gray-600">&lt; 0,2 mm</td>
                          <td className="px-4 py-3"><span className="bg-green-100 text-green-700 px-2 py-1 rounded text-sm">Généralement superficielles</span></td>
                        </tr>
                        <tr className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-700">Fissures</td>
                          <td className="px-4 py-3 text-gray-600">0,2 mm à 1 cm</td>
                          <td className="px-4 py-3"><span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded text-sm">À surveiller</span></td>
                        </tr>
                        <tr className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-700">Lézardes</td>
                          <td className="px-4 py-3 text-gray-600">&gt; 1 cm</td>
                          <td className="px-4 py-3"><span className="bg-red-100 text-red-700 px-2 py-1 rounded text-sm">Danger potentiel</span></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <h3 className="text-xl font-bold text-[#0A2540] mb-4">Quelques formes fréquentes :</h3>
                  
                  <div className="space-y-3">
                    {[
                      { title: "Le faïençage", desc: "Petites craquelures superficielles sur l'enduit, comme une toile d'araignée. En général bénin." },
                      { title: "Les fissures en moustache", desc: "Fines, aux angles des portes et fenêtres. Souvent liées à la maçonnerie qui travaille autour des ouvertures." },
                      { title: "Les fissures en escalier", desc: "Elles suivent les joints de la maçonnerie en marches d'escalier. À prendre au sérieux, car souvent liées à un mouvement du sol." },
                      { title: "Les fissures obliques à 45°", desc: "Typiques d'un tassement du terrain sous une partie de la maison." },
                    ].map((item, i) => (
                      <div key={i} className="bg-white rounded-lg border p-4">
                        <h4 className="font-semibold text-[#0A2540] mb-1">{item.title}</h4>
                        <p className="text-sm text-gray-600">{item.desc}</p>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Section 3 - Alert signs */}
                <section id="section-3" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">3</span>
                    🚨 Les signaux qui doivent alerter
                  </h2>

                  <p className="text-gray-700 mb-6">
                    Certains signes indiquent une possible fissure <strong>structurelle</strong> (qui touche la solidité du bâtiment). 
                    Si vous observez l'un d'eux, faites appel à un professionnel sans tarder :
                  </p>

                  <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-8">
                    <div className="space-y-4">
                      {alertSigns.map((sign, i) => (
                        <div key={i} className="flex items-start gap-3">
                          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{sign.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                    <CheckCircle2 className="w-6 h-6 text-green-500 mb-2" />
                    <p className="text-gray-700">
                      <strong>À l'inverse :</strong> Une fissure fine, superficielle et <strong>stable depuis des années</strong> est 
                      généralement bénigne. Le critère clé est l'<strong>évolution</strong> : une fissure inerte inquiète beaucoup 
                      moins qu'une fissure active qui bouge.
                    </p>
                  </div>
                </section>

                {/* Section 4 - Causes */}
                <section id="section-4" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">4</span>
                    Les causes principales des fissures
                  </h2>

                  <p className="text-gray-700 mb-6">
                    Quatre familles de causes expliquent la grande majorité des fissures en France :
                  </p>

                  <div className="space-y-4 mb-8">
                    {causes.map((cause, i) => (
                      <div key={i} className={`bg-white rounded-xl border-l-4 ${
                        cause.color === 'orange' ? 'border-l-orange-500' :
                        cause.color === 'red' ? 'border-l-red-500' :
                        cause.color === 'blue' ? 'border-l-blue-500' :
                        'border-l-cyan-500'
                      } shadow-sm p-6`}>
                        <div className="flex items-start gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                            cause.color === 'orange' ? 'bg-orange-100' :
                            cause.color === 'red' ? 'bg-red-100' :
                            cause.color === 'blue' ? 'bg-blue-100' :
                            'bg-cyan-100'
                          }`}>
                            <cause.icon className={`w-5 h-5 ${
                              cause.color === 'orange' ? 'text-orange-600' :
                              cause.color === 'red' ? 'text-red-600' :
                              cause.color === 'blue' ? 'text-blue-600' :
                              'text-cyan-600'
                            }`} />
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-[#0A2540] mb-2">{i + 1}. {cause.title}</h3>
                            <p className="text-gray-700">{cause.desc}</p>
                            {cause.link && (
                              <a 
                                href={cause.link} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-[#635BFF] text-sm mt-2 hover:underline"
                              >
                                Vérifiez votre commune sur Géorisques
                                <ExternalLink className="w-4 h-4" />
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Section 5 - What to do */}
                <section id="section-5" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">5</span>
                    Que faire concrètement si vous découvrez une fissure ?
                  </h2>

                  <div className="space-y-6">
                    {[
                      { num: 1, title: "Documentez", desc: "Photographiez la fissure, avec une règle ou un mètre pour l'échelle, et notez la date.", icon: Camera },
                      { num: 2, title: "Surveillez son évolution", desc: "Repérez si elle s'élargit dans le temps. Une simple marque au crayon aux extrémités permet de voir si elle progresse. Cette observation sur quelques semaines/mois est précieuse pour le professionnel.", icon: Eye },
                      { num: 3, title: "Faites appel à un expert", desc: "Dès qu'il y a un doute sur la structure (fissure traversante, évolutive, sur mur porteur), consultez un professionnel : bureau d'études, expert en bâtiment, ou expert fissures. Lui seul peut poser un vrai diagnostic.", icon: Phone },
                    ].map((step) => (
                      <div key={step.num} className="bg-white rounded-xl border p-6 flex items-start gap-4">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#635BFF] to-[#00D4FF] flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                          {step.num}
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-[#0A2540] mb-2 flex items-center gap-2">
                            <step.icon className="w-5 h-5 text-[#635BFF]" />
                            {step.title}
                          </h3>
                          <p className="text-gray-700">{step.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mt-6">
                    <AlertTriangle className="w-6 h-6 text-yellow-600 mb-2" />
                    <p className="text-gray-700">
                      <strong>⚠️ Point essentiel :</strong> Ne rebouchez pas trop vite ! Reboucher une fissure sur un mur qui 
                      bouge encore ne sert à rien — elle réapparaîtra en quelques mois. Il faut d'abord <strong>stabiliser 
                      la cause</strong> avant de réparer.
                    </p>
                  </div>
                </section>

                {/* Section 6 - G5 */}
                <section id="section-6" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">6</span>
                    Le rôle de l'étude de sol (diagnostic géotechnique)
                  </h2>

                  <p className="text-gray-700 mb-6">
                    Quand une fissure est liée à un mouvement du terrain (le cas le plus fréquent pour les fissures graves), 
                    un <strong>diagnostic géotechnique</strong> est souvent nécessaire pour en identifier la cause réelle.
                  </p>

                  <div className="bg-[#635BFF]/10 border border-[#635BFF]/30 rounded-xl p-6 mb-6">
                    <h3 className="text-lg font-bold text-[#0A2540] mb-3">La mission G5</h3>
                    <p className="text-gray-700 mb-4">
                      C'est ce qu'on appelle une <strong>mission G5</strong> selon la norme NF P 94-500 : une étude ciblée 
                      sur un élément géotechnique existant qui pose problème. Elle analyse le sol, les fondations et le 
                      mécanisme à l'origine des désordres.
                    </p>
                    <p className="text-gray-700 mb-4">
                      Son rapport est déterminant : il oriente les réparations et sert aussi de base en cas de 
                      démarche auprès de l'assurance.
                    </p>
                    <Link 
                      to="/guides/comment-choisir-etude-geotechnique"
                      className="inline-flex items-center gap-2 text-[#635BFF] font-semibold hover:underline"
                    >
                      <FileText className="w-4 h-4" />
                      En savoir plus sur les missions G1 à G5
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </section>

                {/* Section 7 - Solutions */}
                <section id="section-7" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">7</span>
                    Les solutions de réparation (selon diagnostic)
                  </h2>

                  <p className="text-gray-700 mb-6">
                    Une fois la cause identifiée et stabilisée, plusieurs techniques existent selon la gravité :
                  </p>

                  <div className="space-y-3 mb-8">
                    {[
                      { title: "Réparation d'enduit / rebouchage", desc: "Pour les fissures superficielles bénignes.", level: "Faible" },
                      { title: "Agrafage / pontage", desc: "Broches inox scellées en travers de la fissure, pour des fissures fines à modérées sur mur porteur.", level: "Moyen" },
                      { title: "Injection de résine", desc: "Époxy ou polyuréthane : renforce et étanchéifie en profondeur.", level: "Moyen" },
                      { title: "Reprise en sous-œuvre", desc: "Micropieux, injections de résine expansive sous les fondations. C'est la solution pour stabiliser des fondations qui bougent réellement.", level: "Élevé" },
                    ].map((solution, i) => (
                      <div key={i} className="bg-white rounded-lg border p-4 flex items-start gap-3">
                        <CheckCircle2 className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                          solution.level === 'Faible' ? 'text-green-500' :
                          solution.level === 'Moyen' ? 'text-yellow-500' :
                          'text-red-500'
                        }`} />
                        <div>
                          <h4 className="font-semibold text-[#0A2540]">{solution.title}</h4>
                          <p className="text-sm text-gray-600">{solution.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="bg-[#00D4FF]/10 border border-[#00D4FF]/30 rounded-xl p-6">
                    <Lightbulb className="w-8 h-8 text-[#00D4FF] mb-3" />
                    <p className="text-[#0A2540] mb-3">
                      <strong>Reprise en sous-œuvre :</strong> C'est un chantier de fondations spéciales, à confier à une 
                      entreprise qualifiée. Le choix dépend entièrement du diagnostic.
                    </p>
                    <Link 
                      to="/guides/comment-choisir-fondations-speciales"
                      className="inline-flex items-center gap-2 text-[#635BFF] font-semibold hover:underline"
                    >
                      <Layers className="w-4 h-4" />
                      Voir notre guide sur les fondations spéciales et micropieux
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </section>

                {/* Section 8 - Insurance */}
                <section id="section-8" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">8</span>
                    Assurance et sécheresse : ce qu'il faut savoir
                  </h2>

                  <div className="bg-white rounded-xl border p-6 mb-6">
                    <Shield className="w-8 h-8 text-[#635BFF] mb-3" />
                    <h3 className="text-lg font-bold text-[#0A2540] mb-3">Catastrophe naturelle (CatNat)</h3>
                    <p className="text-gray-700 mb-4">
                      Les fissures dues au retrait-gonflement des argiles (sécheresse) peuvent être prises en charge par 
                      l'assurance habitation au titre de la <strong>catastrophe naturelle</strong> — à condition qu'un 
                      <strong> arrêté CatNat</strong> ait été pris pour votre commune et l'année concernée.
                    </p>
                    <p className="text-gray-700">
                      C'est la <strong>mairie</strong> qui initie la demande de reconnaissance.
                    </p>
                  </div>

                  <div className="bg-white rounded-xl border p-6">
                    <Shield className="w-8 h-8 text-green-500 mb-3" />
                    <h3 className="text-lg font-bold text-[#0A2540] mb-3">Garantie décennale</h3>
                    <p className="text-gray-700">
                      Pour une construction de moins de 10 ans, la <strong>garantie décennale</strong> du constructeur peut 
                      être engagée si les fissures remettent en cause la solidité de l'ouvrage.
                    </p>
                  </div>
                </section>

                {/* Section 9 - FAQ */}
                <section id="section-9" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">9</span>
                    Questions fréquentes
                  </h2>

                  <div className="space-y-3">
                    {faqs.map((faq, i) => (
                      <div key={i} className="bg-white rounded-xl border overflow-hidden">
                        <button
                          onClick={() => setOpenFaq(openFaq === i ? null : i)}
                          className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                        >
                          <span className="font-semibold text-[#0A2540]">{faq.question}</span>
                          {openFaq === i ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </button>
                        {openFaq === i && (
                          <div className="px-6 pb-4">
                            <p className="text-gray-700">{faq.answer}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </section>

                {/* CTA Section */}
                <section className="bg-gradient-to-br from-[#635BFF] to-[#8B5CF6] rounded-2xl p-8 text-white mb-16">
                  <h2 className="text-2xl font-bold mb-4">Besoin d'être orienté vers le bon professionnel ?</h2>
                  <p className="mb-6 text-white/90">
                    Face à une fissure qui inquiète, le plus important est d'obtenir un <strong>diagnostic fiable</strong> avant 
                    toute réparation — et de s'adresser aux bons spécialistes selon la cause (étude de sol, expertise, reprise en sous-œuvre).
                  </p>
                  <p className="mb-6 text-white/90">
                    GEOTS met en relation les particuliers et les professionnels avec les acteurs de la géotechnique et des 
                    fondations spéciales : bureaux d'études, experts et entreprises spécialisées.
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <Link to="/annuaire/ingenierie-etudes-geotechniques" className="bg-white text-[#635BFF] px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center gap-2">
                      Trouver un expert
                      <ArrowRight className="w-5 h-5" />
                    </Link>
                    <Link to="/etude-geotechnique" className="bg-white/20 text-white px-6 py-3 rounded-lg font-semibold hover:bg-white/30 transition-colors">
                      Demander un diagnostic
                    </Link>
                  </div>
                </section>

                {/* Related Guides */}
                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-[#0A2540] mb-6">Pour aller plus loin</h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    <Link 
                      to="/calcul/prix-etude-de-sol"
                      className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow"
                    >
                      <p className="font-medium text-[#0A2540] mb-2">💰 Estimer le prix d&apos;une étude G5</p>
                      <span className="text-sm text-[#635BFF] flex items-center gap-1">
                        Utiliser l&apos;outil
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </Link>
                    <Link 
                      to="/guides/comment-choisir-etude-geotechnique"
                      className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow"
                    >
                      <p className="font-medium text-[#0A2540] mb-2">Comment choisir son étude géotechnique ?</p>
                      <span className="text-sm text-[#635BFF] flex items-center gap-1">
                        Lire le guide (mission G5 diagnostic)
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </Link>
                    <Link 
                      to="/guides/comment-choisir-fondations-speciales"
                      className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow"
                    >
                      <p className="font-medium text-[#0A2540] mb-2">Comment choisir ses fondations spéciales ?</p>
                      <span className="text-sm text-[#635BFF] flex items-center gap-1">
                        Lire le guide (reprise en sous-œuvre)
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </Link>
                  </div>
                </section>

                {/* Footer note */}
                <div className="text-center text-gray-500 text-sm border-t pt-8">
                  <p>
                    Ce guide est fourni à titre informatif et ne remplace pas l'avis d'un professionnel. 
                    Toute fissure suspectée d'affecter la structure d'un bâtiment doit faire l'objet d'un 
                    diagnostic par un expert qualifié.
                  </p>
                  <p className="mt-2">Dernière mise à jour : 11 juillet 2026</p>
                </div>
              </article>
            </main>
          </div>
        </div>
      </div>
    </>
  );
};

export default HouseCracksGuide;
